import React, { useEffect, useImperativeHandle, useState } from "react";
import { connect } from "react-redux";
import { Slate, Editable } from "slate-react";
import _ from "lodash";

import { uploadFileV2 } from "modules/Services";
import { generateRandomId, getAttachmentSendObj } from "Utils";

import HoveringToolbar from "./HoveringToolbar";
import LeafNode from "./LeafNode";
import Element from "./Element";
import {
  toggleFormat,
  useEditor,
  serializeHTML,
  deserializeHTML,
  addExtraPluginsMemoized,
} from "./InlineEditorUtils";
import {
  styles,
  EDITOR_OPTION_DETAILS,
  ELEMENT_TYPE_IMAGE,
  DEFAULT_EDITOR_TOOLBAR_OPTIONS,
} from "./InlineEditorConstants";
import { Editor, Transforms } from "slate";

const InlineEditor = props => {
  const {
    deserializedValue,
    placeholder,
    onChange,
    editorStyles,
    uploadFileV2,
    mode,
    isUploading,
    plugins,
    editorToolbarOptions,
    onFocusInputCell,
    onBlurInputCell,
    customRef,
    shouldAllowOnDOMBeforeInput,
  } = props;

  const editor = useEditor({ plugins });

  useImperativeHandle(customRef, () => ({
    getEditor: () => editor,
  }));

  // * Currently on upload it will be read-only mode, in enhancement it will be used for async image upload
  const editorData = React.useRef(deserializedValue);
  const uploadData = React.useRef({ uploadIds: [] });

  const isViewMode = _.isEqual(mode, "view");

  useEffect(() => {
    setEditorValue(deserializedValue);
    editorData.current = deserializedValue;
  }, [!_.isEqual(deserializedValue, editorData.current)]);

  useEffect(() => {
    if (isViewMode && !_.isEqual(deserializedValue, editorValue))
      setEditorValue(deserializedValue);
  }, [deserializedValue]);

  const [editorValue, setEditorValue] = useState(deserializedValue);
  useEffect(() => {
    // Delete all entries leaving 1 empty node
    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
    });

    // Removes empty node
    Transforms.removeNodes(editor, {
      at: [0],
    });

    // Insert array of children nodes
    Transforms.insertNodes(editor, editorValue);
  }, [editorValue]);

  const onDOMBeforeInput = event => {
    const inputType = _.get(event, "inputType", "");

    if (_.includes(editorToolbarOptions, inputType)) {
      event.preventDefault();

      const format = _.get(EDITOR_OPTION_DETAILS, [inputType, "format"], "");
      if (format) {
        return toggleFormat(editor, format);
      }
    }
  };

  // * Image's are uploaded in separate thread & update once upload finish
  const uploadAndUpdateImageSilently = async imageElement => {
    const file = _.get(imageElement, "__source.file", null);
    const sendObj = await getAttachmentSendObj(file, `${file.preview}`);
    const fileUrl = await uploadFileV2({
      file,
      attachment: sendObj,
      uploadId: generateRandomId(),
    });

    editorData.current = _.map(editorData.current, element => {
      if (_.get(element, "id") === _.get(imageElement, "id")) {
        return { ...element, url: fileUrl };
      } else {
        return element;
      }
    });

    uploadData.current.uploadIds = _.filter(
      uploadData.current.uploadIds,
      id => id != _.get(imageElement, "id")
    );
    _onChange(editorData.current);
    setEditorValue(editorData.current);
  };

  const _onChange = async newValue => {
    if (_.isEqual(deserializedValue, newValue)) return;

    const sanitizedValue = _.map(newValue, newElement => {
      if (
        _.get(newElement, "type", "") === ELEMENT_TYPE_IMAGE &&
        _.get(newElement, "__source.file", false)
      ) {
        uploadData.current.uploadIds.push(_.get(newElement, "id"));
        uploadAndUpdateImageSilently(newElement);
        return {
          ...newElement,
          __source: null,
        };
      }
      return newElement;
    });

    if (_.get(_.last(sanitizedValue), "type") === "image") {
      // * Last node as image doesn't allow user to input something at end, so adding a empty text node
      sanitizedValue.push({ type: "paragraph", children: [{ text: "" }] });
    }

    editorData.current = sanitizedValue;

    if (!_.isEqual(newValue, sanitizedValue)) {
      editor.children = sanitizedValue;
    }

    const serializedValue = serializeHTML(sanitizedValue);
    onChange(serializedValue);
  };

  // * Update editor value on resize complete
  const onImageResize = (imageId, width) => {
    const [match] = Editor.nodes(editor, {
      match: node => node?.id === imageId,
    });
    if (_.size(match) > 1) {
      Transforms.setNodes(editor, { width }, { at: match[1] });
    }
  };

  const getLeaf = leafProps => {
    return <LeafNode {...leafProps} />;
  };

  const getElement = elementProps => {
    return (
      <Element
        {...elementProps}
        isUploading={
          isUploading &&
          _.includes(
            uploadData.current.uploadIds,
            _.get(elementProps, "element.id", "")
          )
        }
        onImageResize={onImageResize}
        isDisabled={isViewMode}
      />
    );
  };

  return (
    <Slate
      editor={editor}
      initialValue={deserializedValue}
      onChange={_onChange}
    >
      <HoveringToolbar editorToolbarOptions={editorToolbarOptions} />
      <Editable
        disabled={isUploading}
        renderLeaf={getLeaf}
        renderElement={getElement}
        placeholder={placeholder}
        {...(shouldAllowOnDOMBeforeInput && { onDOMBeforeInput })}
        style={{ ...styles.editorStyle, ...editorStyles }}
        readOnly={
          isViewMode ||
          _.find(deserializedValue, item =>
            _.includes(uploadData.current.uploadIds, _.get(item, "id"))
          )
        }
        onFocus={onFocusInputCell}
        onBlur={onBlurInputCell}
      />
    </Slate>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { value, trimValue, plugins = {}, editorToolbarOptions } = ownProps;

  const deserializedValue = deserializeHTML({
    value: value,
    trimValue: trimValue,
  });
  const progressOfUploads = state.app_services.progressOfUploads;
  const isUploading = !_.isEmpty(progressOfUploads);

  return {
    deserializedValue,
    isUploading,
    plugins: addExtraPluginsMemoized({ plugins, editorToolbarOptions }),
  };
};

const mapActionCreators = {
  uploadFileV2,
};

export default connect(mapStateToProps, mapActionCreators)(InlineEditor);
InlineEditor.displayName = "InlineEditor";
InlineEditor.defaultProps = {
  onChange: _.noop,
  mode: "edit",
  plugins: {},
  trimValue: false,
  editorToolbarOptions: DEFAULT_EDITOR_TOOLBAR_OPTIONS,
  onFocusInputCell: _.noop,
  onBlurInputCell: _.noop,
  shouldAllowOnDOMBeforeInput: true,
};
